/**
 * Mail-Text lesbar machen — EINE Stelle für alle Mail-Karten.
 * (28.09.2026, Ticket 2747)
 *
 * Martin: „Die Darstellung der E-Mail sieht katastrophal aus.“ Die gelesene
 * Mail stand als EIN Textblock da: keine Absätze, Aufzählungen im
 * Fließtext, Outlook-Reste („adresse<mailto:adresse>“, „text<https://…>“,
 * „[cid:image001.png@…]“, Linien aus Unterstrichen), die Signatur samt
 * Social-Links und die zitierte frühere Mail ungegliedert dazwischen.
 *
 * Hier wird GEGLIEDERT, nicht gekürzt: Absätze, Listen, Linien, Links.
 * Signatur und frühere Nachricht werden erkannt und EINGEKLAPPT, nicht
 * gelöscht. Weg fallen nur Dinge, die kein Inhalt sind: die cid-Verweise
 * (Platzhalter für eingebettete Bilder, die keine Karte zeigt) und die
 * Markierungen selbst („>“ vor zitierten Zeilen, der Signatur-Trenner „-- “).
 *
 * ⚠️ Nur die ANZEIGE. Das Modell bekommt den Text weiter so, wie
 *    tools/google_workspace.ts ihn liefert.
 * ⚠️ Kein innerHTML — alles über createElement und Textknoten. Links nur mit
 *    http(s)- oder mailto-Ziel, geöffnet mit rel="noopener noreferrer".
 *    Ein Rest mit anderem Ziel („text<javascript:…>“) bleibt Text.
 * ⚠️ Auch HTML-Mails kommen hier als Text an: der Server hat die Tags schon
 *    entfernt, benannte Entities wie „&#8211;“ aber nicht alle aufgelöst.
 *    Darum läuft jeder Text zuerst durch entitiesAufloesen().
 */
import { entitiesAufloesen } from "../../shared/html-text.js";
import { adressenImText } from "../../shared/link-erkennung.js";

/** Ein Stück einer Zeile: Text — oder ein Link, wenn es ein sicheres Ziel gibt. */
interface MailStueck {
  text: string;
  ziel?: string;
}
type Zeile = MailStueck[];

type MailBlock =
  | { art: "absatz"; zeilen: Zeile[] }
  | { art: "liste"; nummeriert: boolean; start: number; punkte: Zeile[] }
  | { art: "linie" }
  | { art: "zitat"; bloecke: MailBlock[] };

interface MailGliederung {
  /** Was die Karte offen zeigt. */
  text: MailBlock[];
  /** Eingeklappt unter „Signatur“. */
  signatur: MailBlock[];
  /** Eingeklappt unter „Frühere Nachricht“. */
  frueher: MailBlock[];
  /** Zeichen im offenen Text — danach richtet sich, ob gekürzt wird. */
  zeichen: number;
}

/**
 * Ab so vielen Zeichen offenem Text wird die Mail auf eine Höhe gekürzt.
 * 20.10.2026 (Albert liest statt zu hören): von 900 auf 1600 — eine übliche Mail steht ganz da, ohne Tippen.
 */
const MAIL_KUERZEN_AB = 1600;

const CID = /\[cid:[^\]]*\]/gi;
/** Outlooks Link-Rest: das Ziel in spitzen Klammern direkt hinter dem Text. */
const LINK_REST = /<((?:https?:\/\/|mailto:)[^<>\s]+)>/gi;
/** Die Beschriftung eines Link-Rests: das Wort direkt davor, ohne öffnende Klammer. */
const BESCHRIFTUNG = /[^\s<>([{„"'«]*$/;
const LEER = /^[\s\u200b\ufeff]*$/;
const LINIE = /^\s*(?:_{5,}|-{5,}|={5,})\s*$/;
const PUNKT = /^\s*[*\-•·]\s+(\S.*)$/;
const NUMMER = /^\s*(\d{1,3})[.)]\s+(\S.*)$/;
const ZITIERT = /^\s*>/;
const SIGNATUR_TRENNER = /^--\s?$/;
const VON = /^\s*\*?(?:von|from)\s*:\*?\s*\S/i;
const GESENDET = /^\s*\*?(?:gesendet|sent|datum|date)\s*:\*?\s*\S/i;
const ALTE_NACHRICHT =
  /^\s*(?:-{2,}\s*(?:ursprüngliche nachricht|original message|weitergeleitete nachricht|forwarded message)\s*-{2,}|(?:anfang der weitergeleiteten nachricht|begin forwarded message)\s*:)\s*$/i;
/** „Am 21.09.2026 um 09:30 schrieb … :“ — mit Ziffer, sonst ist es ein Satz. */
const SCHRIEB = /^\s*(?:am|on)\s.*\d.*\b(?:schrieb|wrote)\b.*:\s*$/i;
/*
 * Die Grußzeile vor der Unterschrift. Outlook kennt keinen Signatur-Trenner;
 * was nach Gruß und Name kommt, ist dort die Signatur. Das ist eine reine
 * Anzeige-Frage (eingeklappt, nicht gelöscht) — ein falscher Treffer kostet
 * einen Tipp, keinen Inhalt. „Grüß Gott“ und „Grüß dich“ sind Anreden,
 * kein Schluss: darum „Grüße/Grüßen/Gruß“, nie „Grüß“ allein.
 * ⚠️ Kein \b hinter „Gruß“: \b kennt kein ß.
 */
const GRUSS =
  /^(?:\p{L}+[\s,]+){0,3}(?:grü(?:ß|ss)(?:e|en)|grue(?:ß|ss)(?:e|en)|gru(?:ß|ss)|regards|lg|glg|vg|mfg|cheers)(?!\p{L})[^\n]{0,40}$/iu;
/** Eine Signatur besteht aus kurzen Zeilen — ein langer Absatz danach ist noch Brief. */
const SIGNATUR_ZEILE_MAX = 80;

/**
 * Zeilenenden vereinheitlichen, Entities auflösen, Bild-Platzhalter
 * entfernen: Outlooks „[cid:…]“ und Apples U+FFFC — beides steht für ein
 * eingebettetes Bild, das keine Karte zeigt.
 */
function vorbereiten(text: string): string {
  return ohneSchreibsteuer(entitiesAufloesen(text.replace(/\r\n?/g, "\n")).replace(CID, "").replace(/\uFFFC/g, ""));
}

/**
 * Schreibrichtungs-Steuerzeichen (U+202A–202E, U+2066–2069, U+200E/F, U+061C) — DIE eine Stelle, die sie aus Fremdtext
 * nimmt. (20.10.2026, Prüfer) Mit ihnen lässt ein Absender „gpj.exe“ als „exe.jpg“ oder eine Adresse rückwärts erscheinen;
 * auf einer Mailkarte braucht sie niemand. Gilt für Text, Anriss, Namen, Adressen und Betreff.
 */
const SCHREIBSTEUER = /[\u202A-\u202E\u2066-\u2069\u200E\u200F\u061C]/g;
export function ohneSchreibsteuer(text: string): string {
  return text.replace(SCHREIBSTEUER, "");
}

/** Nur http(s) und mailto — alles andere ergibt keinen Link. */
export function sicheresZiel(roh: string): string | null {
  const ziel = roh.replace(/^[a-z]+:/i, (s) => s.toLowerCase());
  if (ziel.startsWith("mailto:")) {
    return /^mailto:[^@\s]+@[^@\s?]+(?:\?.*)?$/.test(ziel) ? ziel : null;
  }
  try {
    const u = new URL(ziel);
    return u.protocol === "https:" || u.protocol === "http:" ? u.href : null;
  } catch {
    return null;
  }
}

/** So weit vor einem Link-Rest wird nach seiner Beschriftung gesucht. */
const BESCHRIFTUNG_MAX = 200;

/**
 * Die Beschriftung eines Links — sieht sie selbst wie eine Adresse aus, die woanders hinführt
 * („bank.example<https://boese.example>“), steht das echte Ziel dahinter. (Prüfer 20.10.2026)
 */
function ehrlicheBeschriftung(wort: string, ziel: string): string {
  const echt = anzeigeZiel(ziel);
  if (!wort) return echt;
  if (!/[@.][a-z0-9-]{2,}/i.test(wort)) return wort;
  const norm = (x: string): string =>
    x.toLowerCase().replace(/^(?:https?:\/\/|mailto:)/, "").replace(/^www\./, "").replace(/\/$/, "");
  return norm(echt).startsWith(norm(wort)) ? wort : `${wort} (${echt})`;
}

/** Die Beschriftung, wenn vor dem Link-Rest kein Wort stand. */
function anzeigeZiel(ziel: string): string {
  const s = ziel.startsWith("mailto:")
    ? ziel.slice("mailto:".length).split("?")[0]
    : ziel.replace(/^https?:\/\//, "").replace(/\/$/, "");
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

/** Nackte Adressen im Text verlinken — nach der Hausregel (shared/link-erkennung.ts). */
function mitAdressen(aus: Zeile, text: string): void {
  let zuletzt = 0;
  for (const t of adressenImText(text)) {
    if (t.start > zuletzt) aus.push({ text: text.slice(zuletzt, t.start) });
    const ziel = sicheresZiel(t.ziel);
    aus.push(ziel ? { text: t.text, ziel } : { text: t.text });
    zuletzt = t.start + t.text.length;
  }
  if (zuletzt < text.length) aus.push({ text: text.slice(zuletzt) });
}

/**
 * Eine Zeile in Text und Links zerlegen. „Programm<https://…>“ wird ein Link
 * „Programm“, „a@b.at<mailto:a@b.at>“ ein Link „a@b.at“; steht vor dem Rest
 * kein Wort, trägt der Link die Adresse selbst.
 */
function mailZeile(zeile: string): MailStueck[] {
  const aus: Zeile = [];
  let zuletzt = 0;
  for (const m of zeile.matchAll(LINK_REST)) {
    const start = m.index ?? 0;
    const davor = zeile.slice(zuletzt, start);
    // Nur das Ende von `davor` absuchen (Prüfer 20.10.2026): auf einer langen Zeile ohne Leerzeichen lief der Ausdruck
    // sonst von jeder Startstelle bis zum Link — quadratisch. Ein Wort, das als Beschriftung taugt, ist nie länger.
    const wort = BESCHRIFTUNG.exec(davor.slice(-BESCHRIFTUNG_MAX))?.[0] ?? "";
    mitAdressen(aus, davor.slice(0, davor.length - wort.length));
    const ziel = sicheresZiel(m[1] ?? "");
    aus.push(ziel ? { text: ehrlicheBeschriftung(wort, ziel), ziel } : { text: wort + m[0] });
    zuletzt = start + m[0].length;
  }
  mitAdressen(aus, zeile.slice(zuletzt));
  return aus;
}

/** Eine einzeilige Vorschau (Such-Treffer): dieselbe Bereinigung, nur Text. */
export function mailVorschau(text: string): string {
  return mailZeile(vorbereiten(text).replace(/\s+/g, " "))
    .map((s) => s.text)
    .join("")
    .trim();
}

function naechsteNichtLeere(zeilen: readonly string[], ab: number, bis = zeilen.length): number {
  for (let i = ab; i < bis; i++) if (!LEER.test(zeilen[i] ?? "")) return i;
  return -1;
}

/**
 * Ist die Zeile ein Listenpunkt? Eine einzelne „1. …“-Zeile ist eher ein
 * Satz („1. Oktober passt“) — nummeriert wird erst ab zwei Punkten.
 */
function listenPunkt(
  zeilen: readonly string[],
  i: number,
  nummeriertOffen: boolean,
): { nummeriert: boolean; nr: number; text: string } | null {
  const zeile = zeilen[i] ?? "";
  const p = PUNKT.exec(zeile);
  if (p) return { nummeriert: false, nr: 1, text: p[1] ?? "" };
  const n = NUMMER.exec(zeile);
  if (!n) return null;
  const naechste = naechsteNichtLeere(zeilen, i + 1);
  const mehrere = nummeriertOffen || (naechste >= 0 && NUMMER.test(zeilen[naechste] ?? ""));
  return mehrere ? { nummeriert: true, nr: Number(n[1]), text: n[2] ?? "" } : null;
}

/** Zeilen zu Blöcken: Absätze (Zeilen mit Umbruch), Listen, Linien, Zitate. */
function bloeckeAus(zeilen: readonly string[]): MailBlock[] {
  const aus: MailBlock[] = [];
  let absatz: Extract<MailBlock, { art: "absatz" }> | null = null;
  // `as` statt Anfangswert-Typ: sonst verengt tsc `liste` in der Schleife auf never.
  let liste = null as Extract<MailBlock, { art: "liste" }> | null;
  let zitat: string[] | null = null;
  for (let i = 0; i < zeilen.length; i++) {
    const zeile = zeilen[i] ?? "";
    const naechste = naechsteNichtLeere(zeilen, i + 1);
    const weiterZitiert = naechste >= 0 && ZITIERT.test(zeilen[naechste] ?? "");
    // Zitierte Zeilen bleiben beisammen, auch über eine Leerzeile hinweg.
    if (ZITIERT.test(zeile) || (zitat && LEER.test(zeile) && weiterZitiert)) {
      absatz = null;
      liste = null;
      (zitat ??= []).push(zeile.replace(/^\s*>\s?/, ""));
      continue;
    }
    if (zitat) {
      aus.push({ art: "zitat", bloecke: bloeckeAus(zitat) });
      zitat = null;
    }
    if (LEER.test(zeile)) {
      absatz = null;
      // Outlook setzt Leerzeilen zwischen Punkte — die Liste bleibt offen,
      // wenn der nächste Punkt dieselbe Art hat.
      const folgt = naechste >= 0 ? (zeilen[naechste] ?? "") : "";
      if (liste && !(liste.nummeriert ? NUMMER.test(folgt) : PUNKT.test(folgt))) liste = null;
      continue;
    }
    if (LINIE.test(zeile)) {
      absatz = null;
      liste = null;
      aus.push({ art: "linie" });
      continue;
    }
    const punkt = listenPunkt(zeilen, i, liste?.nummeriert === true);
    if (punkt) {
      absatz = null;
      if (liste?.nummeriert !== punkt.nummeriert) {
        liste = { art: "liste", nummeriert: punkt.nummeriert, start: punkt.nr, punkte: [] };
        aus.push(liste);
      }
      liste.punkte.push(mailZeile(punkt.text.trim()));
      continue;
    }
    // Eingerückte Fortsetzung des letzten Punkts (umbrochener Listentext).
    const letzter = liste?.punkte[liste.punkte.length - 1];
    if (letzter && /^\s{2,}\S/.test(zeile)) {
      letzter.push({ text: " " }, ...mailZeile(zeile.trim()));
      continue;
    }
    liste = null;
    if (!absatz) {
      absatz = { art: "absatz", zeilen: [] };
      aus.push(absatz);
    }
    absatz.zeilen.push(mailZeile(zeile.trim()));
  }
  if (zitat) aus.push({ art: "zitat", bloecke: bloeckeAus(zitat) });
  return aus;
}

/** Eine Linie am Rand eines Abschnitts trennt nichts. */
function ohneRandLinien(bloecke: MailBlock[]): MailBlock[] {
  let von = 0;
  let bis = bloecke.length;
  while (von < bis && bloecke[von]?.art === "linie") von++;
  while (bis > von && bloecke[bis - 1]?.art === "linie") bis--;
  return bloecke.slice(von, bis);
}

/**
 * Wo die zitierte frühere Nachricht beginnt — oder -1. Die früheste Marke
 * gilt: Outlook-Kopf („Von: … Gesendet: …“), „-----Ursprüngliche
 * Nachricht-----“, „Am … schrieb …:“ (auch auf zwei Zeilen umbrochen) oder
 * ein Schluss, der nur noch aus „>“-Zeilen besteht. Outlooks Trennlinie
 * davor bleibt am Ende des Abschnitts davor — ohneRandLinien() nimmt sie.
 */
function zitatBeginn(zeilen: readonly string[]): number {
  for (let i = 0; i < zeilen.length; i++) {
    const z = zeilen[i] ?? "";
    const kopf = VON.test(z) && zeilen.slice(i + 1, i + 5).some((n) => GESENDET.test(n));
    const zuschreibung =
      /^\s*(?:am|on)\s/i.test(z) && (SCHRIEB.test(z) || SCHRIEB.test(`${z} ${zeilen[i + 1] ?? ""}`));
    const nurNochZitat = ZITIERT.test(z) && zeilen.slice(i).every((n) => LEER.test(n) || ZITIERT.test(n));
    if (kopf || zuschreibung || nurNochZitat || ALTE_NACHRICHT.test(z)) return i;
  }
  return -1;
}

/**
 * Wo die Signatur beginnt: nach „-- “, sonst nach Grußzeile und Name.
 * `textBis` = Ende des offenen Textes, `ab` = erste Zeile der Signatur.
 */
function signaturGrenze(zeilen: readonly string[], ende: number): { textBis: number; ab: number } | null {
  for (let i = ende - 1; i >= 0; i--) {
    if (SIGNATUR_TRENNER.test(zeilen[i] ?? "")) {
      return naechsteNichtLeere(zeilen, i + 1, ende) >= 0 ? { textBis: i, ab: i + 1 } : null;
    }
  }
  // Die erste Zeile ist die Anrede, nie der Schluss.
  const erste = naechsteNichtLeere(zeilen, 0, ende);
  for (let i = ende - 1; i > erste; i--) {
    if (!GRUSS.test((zeilen[i] ?? "").trim())) continue;
    const name = naechsteNichtLeere(zeilen, i + 1, ende);
    const ab = name < 0 ? -1 : naechsteNichtLeere(zeilen, name + 1, ende);
    if (ab < 0) return null;
    const kopf = zeilen.slice(ab, ende).filter((z) => !LEER.test(z)).slice(0, 3);
    return kopf.every((z) => z.trim().length <= SIGNATUR_ZEILE_MAX) ? { textBis: ab, ab } : null;
  }
  return null;
}

/** Den Mail-Text in offenen Text, Signatur und frühere Nachricht gliedern. */
export function gliedereMail(body: string): MailGliederung {
  const zeilen = vorbereiten(body).split("\n");
  const zitat = zitatBeginn(zeilen);
  const ende = zitat < 0 ? zeilen.length : zitat;
  const sig = signaturGrenze(zeilen, ende);
  const offen = zeilen.slice(0, sig ? sig.textBis : ende);
  let frueher = zitat < 0 ? [] : ohneRandLinien(bloeckeAus(zeilen.slice(zitat)));
  // Nur „>“-Zeilen: der Abschnitt IST das Zitat — keine zweite Einrückung.
  if (frueher.length === 1 && frueher[0]?.art === "zitat") frueher = frueher[0].bloecke;
  return {
    text: ohneRandLinien(bloeckeAus(offen)),
    signatur: sig ? ohneRandLinien(bloeckeAus(zeilen.slice(sig.ab, ende))) : [],
    frueher,
    zeichen: offen.join(" ").replace(/\s+/g, " ").trim().length,
  };
}

function zeileAnhaengen(ziel: HTMLElement, zeile: Zeile): void {
  for (const s of zeile) {
    if (!s.ziel) {
      ziel.appendChild(document.createTextNode(s.text));
      continue;
    }
    const a = document.createElement("a");
    a.href = s.ziel;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = s.text;
    ziel.appendChild(a);
  }
}

function zeigeBloecke(ziel: HTMLElement, bloecke: readonly MailBlock[]): void {
  for (const b of bloecke) {
    if (b.art === "absatz") {
      const p = document.createElement("p");
      b.zeilen.forEach((z, i) => {
        if (i > 0) p.appendChild(document.createElement("br"));
        zeileAnhaengen(p, z);
      });
      ziel.appendChild(p);
    } else if (b.art === "liste") {
      const l = document.createElement(b.nummeriert ? "ol" : "ul");
      if (b.nummeriert && b.start !== 1) l.setAttribute("start", String(b.start));
      for (const punkt of b.punkte) {
        const li = document.createElement("li");
        zeileAnhaengen(li, punkt);
        l.appendChild(li);
      }
      ziel.appendChild(l);
    } else if (b.art === "linie") {
      const hr = document.createElement("hr");
      hr.className = "tool-mail-linie";
      ziel.appendChild(hr);
    } else {
      const q = document.createElement("blockquote");
      q.className = "tool-mail-zitat";
      zeigeBloecke(q, b.bloecke);
      ziel.appendChild(q);
    }
  }
}

function falte(titel: string, bloecke: readonly MailBlock[], offen: boolean): HTMLDetailsElement {
  const d = document.createElement("details");
  d.className = "tool-mail-falte";
  d.open = offen;
  const s = document.createElement("summary");
  s.textContent = titel;
  const inhalt = document.createElement("div");
  inhalt.className = "tool-mail-falte-inhalt";
  zeigeBloecke(inhalt, bloecke);
  d.append(s, inhalt);
  return d;
}

let textNr = 0;

/**
 * Welche Mails ganz aufgeklappt wurden — je Mail-Kennung, nur auf diesem Gerät. (20.10.2026)
 * Nur die Kennung, nie Text. Ohne Speicher (privates Fenster) gilt einfach „gekürzt“.
 * Ein Kontowechsel leert den Schlüssel (frontend/app.ts, wie den Verlauf).
 */
export const MAIL_OFFEN_SCHLUESSEL = "vespera.mail-offen";
const OFFEN_MAX = 50;

function offeneMails(): string[] {
  try {
    const roh: unknown = JSON.parse(localStorage.getItem(MAIL_OFFEN_SCHLUESSEL) ?? "[]");
    return Array.isArray(roh) ? roh.filter((k): k is string => typeof k === "string") : [];
  } catch {
    return [];
  }
}

function merkeOffen(kennung: string, offen: boolean): void {
  try {
    const rest = offeneMails().filter((k) => k !== kennung);
    if (offen) rest.push(kennung);
    localStorage.setItem(MAIL_OFFEN_SCHLUESSEL, JSON.stringify(rest.slice(-OFFEN_MAX)));
  } catch {
    /* kein Speicher — der Zustand gilt bis zum Neuladen */
  }
}

/**
 * Lange Mails auf eine Höhe kürzen (CSS), mit „Ganze Mail anzeigen“.
 * EIN Schalter setzt Klasse, aria-expanded und Beschriftung zusammen.
 */
function kuerzen(karte: HTMLElement, text: HTMLElement, kennung?: string): void {
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = "tool-mail-mehr";
  knopf.setAttribute("aria-controls", text.id);
  const setze = (offen: boolean): void => {
    text.classList.toggle("tool-mail-text--gekuerzt", !offen);
    knopf.setAttribute("aria-expanded", String(offen));
    knopf.textContent = offen ? "Weniger anzeigen" : "Ganze Mail anzeigen";
  };
  knopf.addEventListener("click", () => {
    const offen = knopf.getAttribute("aria-expanded") !== "true";
    setze(offen);
    if (kennung) merkeOffen(kennung, offen);
  });
  setze(kennung !== undefined && offeneMails().includes(kennung));
  karte.appendChild(knopf);
  // Passt der Text auf einem breiten Schirm doch in die Höhe, gibt es nichts
  // aufzuklappen. Nur mit echter Anordnung prüfbar (Höhe > 0).
  requestAnimationFrame(() => {
    if (text.classList.contains("tool-mail-text--gekuerzt") && text.clientHeight > 0 && text.scrollHeight <= text.clientHeight + 1) {
      setze(true);
      knopf.remove();
    }
  });
}

/**
 * Den Mail-Text in die Karte setzen: offener Text, dann Signatur und frühere Nachricht eingeklappt.
 * Nur Textknoten und geprüfte Links (sicheresZiel: http, https, mailto) — nie HTML aus der Mail, nie ein Bild.
 * `kennung` (die Gmail-Kennung) merkt sich das Aufklappen über das Neuladen hinaus.
 */
export function zeigeMailText(karte: HTMLElement, body: string, kennung?: string): void {
  const g = gliedereMail(body);
  if (g.text.length > 0) {
    const text = document.createElement("div");
    text.className = "tool-mail-text";
    text.id = `tool-mail-text-${++textNr}`;
    zeigeBloecke(text, g.text);
    karte.appendChild(text);
    if (g.zeichen > MAIL_KUERZEN_AB) kuerzen(karte, text, kennung);
  }
  if (g.signatur.length > 0) karte.appendChild(falte("Signatur", g.signatur, false));
  // Ohne eigenen Text (reine Weiterleitung) IST die frühere Nachricht der Inhalt.
  if (g.frueher.length > 0) karte.appendChild(falte("Frühere Nachricht", g.frueher, g.text.length === 0));
}

/** Absender oder Empfänger: „Name <adresse>“ oder nur die Adresse. */
interface MailPerson {
  name: string;
  adresse: string;
}

/** Einen Adresskopf in Personen zerlegen — Kommas in "Nachname, Vorname" trennen nicht. */
export function mailPersonen(kopf: string): MailPerson[] {
  const teile: string[] = [];
  let aktuell = "";
  let inAnfuehrung = false;
  let inWinkel = false;
  for (const c of kopf) {
    if (c === '"') inAnfuehrung = !inAnfuehrung;
    else if (c === "<" && !inAnfuehrung) inWinkel = true;
    else if (c === ">" && !inAnfuehrung) inWinkel = false;
    if ((c === "," || c === ";") && !inAnfuehrung && !inWinkel) {
      teile.push(aktuell);
      aktuell = "";
      continue;
    }
    aktuell += c;
  }
  teile.push(aktuell);
  const aus: MailPerson[] = [];
  for (const teil of teile) {
    const s = teil.trim();
    if (!s) continue;
    const m = /^(.*?)<([^<>]*)>\s*$/.exec(s);
    const name = ohneSchreibsteuer((m ? (m[1] ?? "") : "").trim().replace(/^"(.*)"$/, "$1").replace(/\\"/g, '"')).trim();
    aus.push({ name, adresse: ohneSchreibsteuer((m ? (m[2] ?? "") : s).trim()) });
  }
  return aus;
}

/**
 * Der Name, sonst die Adresse. Trägt der Name selbst ein „@“ und weicht von der echten Adresse ab
 * („chef@firma.example“ <x@boese.example>), steht die echte Adresse dahinter. (Prüfer 20.10.2026)
 */
function personKurz(p: MailPerson): string {
  if (!p.name) return p.adresse;
  if (p.name.includes("@") && p.adresse && p.name.toLowerCase() !== p.adresse.toLowerCase()) return `${p.name} (${p.adresse})`;
  return p.name;
}

export function mailPersonKurz(kopf: string): string {
  const p = mailPersonen(kopf)[0];
  return p ? personKurz(p) : "";
}

/** Empfänger knapp: bis zu drei Namen, dann „+N“. */
export function mailEmpfaengerKurz(kopf: string): string {
  const namen = mailPersonen(kopf).map(personKurz);
  const zuViel = namen.length - 3;
  return zuViel > 0 ? `${namen.slice(0, 3).join(", ")} +${zuViel}` : namen.join(", ");
}

const WIEN = "Europe/Vienna";

/**
 * Das Mail-Datum deutsch, in Wiener Zeit: „Di., 22. September um 10:15“,
 * das Jahr nur, wenn es nicht das laufende ist. Unlesbares bleibt, wie es war.
 */
export function mailDatum(roh: string, jetzt = new Date()): { text: string; iso: string } | null {
  const s = roh.replace(/\s*\([^)]*\)\s*$/, "").trim();
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return { text: roh.trim(), iso: "" };
  const jahr = (x: Date): string => x.toLocaleDateString("de-AT", { timeZone: WIEN, year: "numeric" });
  const text = d.toLocaleString("de-AT", {
    timeZone: WIEN,
    weekday: "short",
    day: "numeric",
    month: "long",
    ...(jahr(d) === jahr(jetzt) ? {} : { year: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
  });
  return { text, iso: d.toISOString() };
}
